import {useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {ArrowLeftIcon, BuildingOffice2Icon} from "@heroicons/react/24/outline";
import Shell from "../../../../../components/specific/Shell.tsx";
import {User} from "../../../../../api/accessControl/User.ts";
import {BackendApi} from "../../../../../api/api.ts";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import BusinessRegistrationFormFields from "../../../../../components/business/BusinessRegistrationFormFields.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {
    BusinessRegistrationFormData,
    EMPTY_BUSINESS_REGISTRATION_FORM,
    toCreateBusinessApiPayload,
    validateBusinessRegistrationForm,
} from "../../../../../lib/businessRegistration.ts";

export default function PartnerAddBusinessPage({currentUser}: { currentUser: User }) {
    const navigate = useNavigate();
    const {dispatch} = useNotifications();
    const api = new BackendApi(currentUser.getToken());
    const [formData, setFormData] = useState<BusinessRegistrationFormData>(EMPTY_BUSINESS_REGISTRATION_FORM);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    const updateField = <K extends keyof BusinessRegistrationFormData>(
        field: K,
        value: BusinessRegistrationFormData[K],
    ) => {
        setFormData((prev) => ({...prev, [field]: value}));
        if (errors[field]) {
            setErrors((prev) => ({...prev, [field]: ""}));
        }
    };

    const handleSubmit = async () => {
        const validationErrors = validateBusinessRegistrationForm(formData);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            const response = await api.Partner__createBusiness(toCreateBusinessApiPayload(formData));
            dispatch({
                title: 'Bedrijf toegevoegd',
                description: 'Het klantenbedrijf is succesvol aangemaakt.',
                type: 'success',
            });
            navigate(`/business/${response.id}?access=open`);
        } catch {
            dispatch({
                title: "Fout opgetreden",
                description: "Bedrijf toevoegen mislukt. Probeer het opnieuw.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Shell currentUser={currentUser} isLoading={false}>
            <div className="mb-8">
                <Link
                    to="/"
                    className="inline-flex items-center p-2 rounded-lg hover:bg-gray-100 transition-colors mb-6"
                >
                    <ArrowLeftIcon className="h-5 w-5 text-gray-500 mr-2"/>
                    Dashboard
                </Link>

                <div className="flex items-center">
                    <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <BuildingOffice2Icon className="h-8 w-8 text-gray-600"/>
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Bedrijf toevoegen</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Voeg een nieuw bedrijf toe
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl">
                <form
                    className="space-y-6"
                    onSubmit={(event) => {
                        event.preventDefault();
                        void handleSubmit();
                    }}
                >
                    <BusinessRegistrationFormFields
                        formData={formData}
                        errors={errors}
                        loading={loading}
                        onFieldChange={updateField}
                    />

                    <div className="border border-blue-100 bg-blue-50 rounded-md p-3 text-sm text-gray-800">
                        Als je hier een bedrijf toevoegt, dan wordt je klant nog niet uitgenodigd. Je kan wel vast
                        document historie uploaden (via API, webapp of smart mailbox)
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                        <PrimaryButton
                            title={loading ? "Bezig..." : "Bedrijf toevoegen"}
                            onClick={() => void handleSubmit()}
                            disabled={loading}
                        />
                    </div>
                </form>
            </div>
        </Shell>
    );
}
